import React, { useEffect } from "react";
import { Modal, Form, Input, InputNumber, Checkbox, Button } from "antd";
import { IFaq, FaqFormValues } from "../../../hooks/api/faq/callbacks";
import { useFaqCreate, useFaqUpdate } from "../../../hooks/api/faq/hooks";
import { useMessageApi } from "../../../provider/AntdNotification";

interface FaqModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItem?: IFaq | null;
  qData?: any;
}

const FaqModal: React.FC<FaqModalProps> = ({
  isOpen,
  onClose,
  selectedItem,
  qData,
}) => {
  const messageApi = useMessageApi();
  const [form] = Form.useForm<FaqFormValues>();
  const createMutation = useFaqCreate({ qData });
  const updateMutation = useFaqUpdate({ qData });

  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (isOpen) {
      if (selectedItem) {
        form.setFieldsValue({
          question: selectedItem.question,
          answer: selectedItem.answer,
          order: selectedItem.order ?? 0,
          status: selectedItem.status ?? true,
        });
      } else {
        form.setFieldsValue({
          question: "",
          answer: "",
          order: 0,
          status: true,
        });
      }
    }
  }, [isOpen, selectedItem, form]);

  const handleSubmit = (values: FaqFormValues) => {
    if (selectedItem) {
      updateMutation.mutate(
        { id: selectedItem._id, data: values },
        {
          onSuccess: () => {
            messageApi.success("FAQ updated successfully");
            onClose();
          },
          onError: (err: any) =>
            messageApi.error(err?.response?.data?.message || err?.message || "Failed to update FAQ"),
        }
      );
    } else {
      createMutation.mutate(values, {
        onSuccess: () => {
          messageApi.success("FAQ created successfully");
          onClose();
        },
        onError: (err: any) =>
          messageApi.error(err?.response?.data?.message || err?.message || "Failed to create FAQ"),
      });
    }
  };

  return (
    <Modal
      title={selectedItem ? "Edit FAQ" : "Add FAQ"}
      open={isOpen}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isPending}
          onClick={() => form.submit()}
        >
          {selectedItem ? "Update FAQ" : "Save FAQ"}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="mt-4 flex flex-col gap-3"
      >
        <Form.Item
          name="question"
          label="Question"
          rules={[{ required: true, message: "Please enter question" }]}
        >
          <Input placeholder="e.g. What is the ArtParkS Guarantee?" />
        </Form.Item>

        <Form.Item
          name="answer"
          label="Answer"
          rules={[{ required: true, message: "Please enter answer" }]}
        >
          <Input.TextArea rows={4} placeholder="Enter answer details..." />
        </Form.Item>

        <Form.Item name="order" label="Display Order">
          <InputNumber min={0} className="w-full" placeholder="e.g. 1" />
        </Form.Item>

        <Form.Item name="status" valuePropName="checked">
          <Checkbox>Active Status</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default FaqModal;
